/**
 * [PURPOSE]: Data-driven class resource definitions
 * [WHY]: Enables RestPanel, ClassResourcesPanel, and custom classes to share
 *        a single schema for class resources (Ki, Rage, Channel Divinity, etc.)
 * [INTEGRATION]: Used by RestPanel for rest restoration, ClassResourcesPanel for display,
 *                and CustomClassCreator for defining custom class resources.
 *
 * Resource schema:
 *   id:             string  — Storage key in classEntry (e.g., 'ki_points')
 *   name:           string  — Display name
 *   icon:           string  — Emoji icon
 *   rechargeOn:     'short_rest' | 'long_rest'
 *   minLevel:       number  — Class level required to gain this resource (default 1)
 *   maxCalculation: string  — How to compute max uses:
 *     'classLevel'        → max = classLevel
 *     'classLevel*5'      → max = classLevel * 5 (Lay on Hands)
 *     'fixed:1'           → max = 1 (Second Wind)
 *     'chaMod:1'          → max = max(1, CHA modifier)
 *     'wisMod:1'          → max = max(1, WIS modifier)
 *     'intMod:1'          → max = max(1, INT modifier) — Artificer Flash of Genius
 *     'intMod2x'          → max = max(1, 2 × INT modifier) — Artificer Spell-Storing Item
 *     'wizardLevel*2+intMod' → max = max(1, classLevel*2 + INT) — Wizard Arcane Ward
 *     'table'             → max from a lookup table (uses maxTable)
 *     'levelTable'        → max from maxTable keyed by classLevel thresholds
 *   maxTable:       object  — For 'levelTable': { classLevel: maxUses }
 *   color:          string  — UI color theme
 *   description:    string  — Tooltip description
 *   subclass:       string? — Only applies to this subclass (e.g., 'Battle Master')
 *   subclassKey:    string? — Storage path within subclassData (e.g., 'superiorityDice')
 *   dieSize:        object? — For resources with dice: { classLevel: dieSize }
 *   conditionalRecharge: string? — e.g., 'bardLevel5' for Bard Font of Inspiration
 */

// [PURPOSE]: Resource definitions for all SRD classes
// [WHY]: Single source of truth — replaces hardcoded class name checks in RestPanel + ClassResourcesPanel
export const CLASS_RESOURCE_DEFINITIONS = {
  Barbarian: [
    {
      id: 'rage',
      name: 'Rage',
      icon: '\uD83D\uDCA2', // 💢
      rechargeOn: 'long_rest',
      minLevel: 1,
      maxCalculation: 'levelTable',
      maxTable: { 1: 2, 3: 3, 6: 4, 12: 5, 17: 6, 20: 999 },
      color: 'red',
      description: 'Bonus damage and damage resistance'
    }
  ],

  Bard: [
    {
      id: 'bardic_inspiration',
      name: 'Bardic Inspiration',
      icon: '\uD83C\uDFB5', // 🎵
      rechargeOn: 'long_rest',
      conditionalRecharge: 'short_rest_at_5', // Font of Inspiration at level 5
      minLevel: 1,
      maxCalculation: 'chaMod:1',
      dieSize: { 1: 'd6', 5: 'd8', 10: 'd10', 15: 'd12' },
      color: 'pink',
      description: 'Allies add die to attack, ability check, or save'
    }
  ],

  Cleric: [
    {
      id: 'channel_divinity',
      name: 'Channel Divinity',
      icon: '\u271D\uFE0F', // ✝️
      rechargeOn: 'short_rest',
      minLevel: 2,
      maxCalculation: 'levelTable',
      maxTable: { 2: 1, 6: 2, 18: 3 },
      color: 'cyan',
      description: 'Divine power for special abilities'
    }
  ],

  Fighter: [
    {
      id: 'second_wind',
      name: 'Second Wind',
      icon: '\uD83D\uDC9A', // 💚
      rechargeOn: 'short_rest',
      minLevel: 1,
      maxCalculation: 'fixed:1',
      color: 'green',
      description: 'Heal 1d10 + level HP as bonus action'
    },
    {
      id: 'action_surge',
      name: 'Action Surge',
      icon: '\u26A1', // ⚡
      rechargeOn: 'short_rest',
      minLevel: 2,
      maxCalculation: 'levelTable',
      maxTable: { 2: 1, 17: 2 },
      color: 'orange',
      description: 'Take additional action on your turn'
    },
    {
      // [Phase 4b bucket 2 — APR 2026]: Indomitable (PHB p.72) — reroll a failed save.
      // Scales 1 use → 2 → 3 at L9/13/17. Long rest recharge.
      id: 'indomitable',
      name: 'Indomitable',
      icon: '🛡️', // 🛡️
      rechargeOn: 'long_rest',
      minLevel: 9,
      maxCalculation: 'levelTable',
      maxTable: { 9: 1, 13: 2, 17: 3 },
      color: 'red',
      description: 'Reroll a failed save'
    }
  ],

  Monk: [
    {
      id: 'ki_points',
      name: 'Ki Points',
      icon: '\u262F\uFE0F', // ☯️
      rechargeOn: 'short_rest',
      minLevel: 2,
      maxCalculation: 'classLevel',
      color: 'blue',
      description: 'Fuel for martial arts abilities'
    }
  ],

  Paladin: [
    {
      id: 'channel_divinity',
      name: 'Channel Divinity',
      icon: '\u271D\uFE0F', // ✝️
      rechargeOn: 'short_rest',
      minLevel: 3,
      maxCalculation: 'fixed:1',
      color: 'cyan',
      description: 'Divine power for special abilities'
    },
    {
      id: 'lay_on_hands',
      name: 'Lay on Hands',
      icon: '\uD83D\uDE4F', // 🙏
      rechargeOn: 'long_rest',
      minLevel: 1,
      maxCalculation: 'classLevel*5',
      color: 'gold',
      description: 'Healing pool (HP or cure disease/poison)',
      // [Sub-plan 1H-G-bw Stone 4 -- 2026-05-30]: spendHpModal:true marks this
      // resource as a "HP pool" -- ClassResourcesPanel renders a "Spend HP"
      // affordance that opens UsableAbilityModal. Rule 11 compliant: the UI
      // checks this boolean PROPERTY (not a string class/resource name).
      // Any custom-class resource pool with this flag set gets the same
      // affordance automatically (CW-replicable).
      spendHpModal: true,
    }
  ],

  // [Phase 4b bucket 2 — APR 2026]: Druid Wild Shape resource. PHB p.66.
  // 2 uses from L2; Archdruid (L20) feature grants unlimited uses (PHB p.67).
  // [Q17 FIX 2026-05-16]: Migrated from `maxCalculation: 'fixed:2'` to the
  //   `levelTable` shape with a 999 sentinel at L20, matching Barbarian rage
  //   (`{20: 999}` line 42). The combat panel / RestPanel display 999 as
  //   "Unlimited" in the resource pill — the schema models it cleanly now.
  //   Reference: docs/audits/classes/2026-05-16-druid-progression.md F-2.
  Druid: [
    {
      id: 'wild_shape',
      name: 'Wild Shape',
      icon: '🐾', // 🐾
      rechargeOn: 'short_rest',
      minLevel: 2,
      maxCalculation: 'levelTable',
      maxTable: { 2: 2, 20: 999 },
      color: 'green',
      description: 'Transform into a beast you have seen'
    }
  ],

  Sorcerer: [
    {
      id: 'sorcery_points',
      name: 'Sorcery Points',
      icon: '\u2728', // ✨
      rechargeOn: 'long_rest',
      minLevel: 2,
      maxCalculation: 'classLevel',
      color: 'purple',
      description: 'Metamagic and spell slot conversion'
    }
  ],

  Warlock: [
    {
      id: 'pact_magic',
      name: 'Pact Magic',
      icon: '\uD83D\uDD2E', // 🔮
      rechargeOn: 'short_rest',
      minLevel: 1,
      maxCalculation: 'levelTable',
      maxTable: { 1: 1, 2: 2, 11: 3, 17: 4 },
      // [PURPOSE]: Slot level display — ClassResourcesPanel reads this for dynamic name
      slotLevelTable: { 1: 1, 3: 2, 5: 3, 7: 4, 9: 5 },
      color: 'purple',
      description: 'All slots recharge on short rest'
    }
  ],

  // Warlock subclass resources
  // [Phase 4b bucket 3 — Warlock Task 13]: Celestial Healing Light pool (XGE p.55).
  // Pool of d6 healing dice equal to 1 + Warlock level, recovered on long rest.
  'Warlock:The Celestial': [
    {
      id: 'healing_light_dice',
      name: 'Healing Light',
      icon: '\u{2728}', // ✨
      rechargeOn: 'long_rest',
      minLevel: 1,
      // 1 + Warlock level — modeled as 'classLevel' + bonus 1 baked in by name.
      // The maxCalculation vocabulary doesn't include 'classLevel+1'; we use the
      // closest fit ('classLevel') and document the +1 in the description so the
      // RestPanel reader can apply +1 in the consumer. [BREADCRUMB] Add
      // 'classLevel+1' formula for tighter resource-max parity (BC-22).
      maxCalculation: 'classLevel',
      dieSize: { 1: 'd6' },
      color: 'yellow',
      description: 'd6 healing dice (1 + Warlock level); spend up to CHA mod per use'
    }
  ],

  // Fighter subclass resources
  'Fighter:Battle Master': [
    {
      id: 'superiority_dice',
      name: 'Superiority Dice',
      icon: '\u2694\uFE0F', // ⚔️
      rechargeOn: 'short_rest',
      minLevel: 3,
      maxCalculation: 'levelTable',
      maxTable: { 3: 4, 7: 5, 15: 6 },
      subclassKey: 'superiorityDice',
      dieSize: { 3: 'd8', 10: 'd10', 18: 'd12' },
      color: 'yellow',
      description: 'Roll when using maneuvers'
    }
  ],

  'Fighter:Psi Warrior': [
    {
      id: 'psionic_energy_dice',
      name: 'Psionic Energy Dice',
      icon: '\u{1F9E0}', // 🧠
      rechargeOn: 'short_rest',
      minLevel: 3,
      maxCalculation: 'levelTable',
      maxTable: { 3: 4, 5: 6, 11: 8, 17: 12 },
      dieSize: { 3: 'd6', 5: 'd8', 11: 'd10', 17: 'd12' },
      color: 'purple',
      description: 'Power source for psi warrior abilities'
    }
  ],

  'Fighter:Samurai': [
    {
      id: 'fighting_spirit',
      name: 'Fighting Spirit',
      icon: '⚔️', // ⚔️
      rechargeOn: 'long_rest',
      // [BREADCRUMB] Tireless Spirit at L10 regains an expended use on initiative roll.
      // The conditionalRecharge vocabulary does not yet support 'short_rest_at_3' or
      // 'on_initiative_at_10' — modeled as long_rest only. Tracked as BC follow-up.
      minLevel: 3,
      maxCalculation: 'fixed:3',
      color: 'red',
      description: 'Bonus action self-buff: temp HP + advantage on attacks'
    }
  ],

  // Cleric subclass resources
  'Cleric:War Domain': [
    {
      id: 'war_priest_attacks',
      name: 'War Priest Attacks',
      icon: '⚔️', // ⚔️
      rechargeOn: 'long_rest',
      // [BREADCRUMB] PHB p.59: At L18, regain all expended uses on a short or long rest.
      //              The conditionalRecharge vocabulary does not yet support
      //              'short_rest_at_18' — modeled as long_rest only. Tracked as BC.
      minLevel: 1,
      maxCalculation: 'wisMod:1',
      color: 'red',
      description: 'Bonus action weapon attack after taking the Attack action; uses = WIS mod (min 1)'
    }
  ],

  // Rogue subclass resources
  'Rogue:Soulknife': [
    {
      id: 'psionic_energy_dice',
      name: 'Psionic Energy Dice',
      icon: '\u{1F9E0}', // 🧠
      rechargeOn: 'short_rest',
      minLevel: 3,
      maxCalculation: 'levelTable',
      maxTable: { 3: 4, 5: 6, 11: 8, 17: 12 },
      dieSize: { 3: 'd6', 5: 'd8', 11: 'd10', 17: 'd12' },
      color: 'purple',
      description: 'Power source for Soulknife abilities (Psi-Bolstered Knack, Psychic Whispers, Homing Strikes, Psychic Teleportation, Rend Mind)'
    }
  ],

  // Wizard subclass resources
  'Wizard:School of Divination': [
    {
      id: 'portent_dice',
      name: 'Portent Dice',
      icon: '\u{1F52E}', // 🔮
      rechargeOn: 'long_rest',
      minLevel: 2,
      maxCalculation: 'fixed:2',
      // [BREADCRUMB] At L14 Greater Portent: roll 3 dice instead of 2. The
      // maxCalculation vocabulary does not yet support a per-level table for
      // this case (similar to Bard inspiration); modeled as fixed:2 only and
      // the L14 step-up surfaces only via the feature flag in srdSubclassFeatures.
      color: 'indigo',
      description: 'Roll 2d20 at long rest (3 at L14); replace any creature\'s d20 roll with a portent die'
    }
  ],

  // [Phase 4b BC-9-style closeout — APR 2026]: Wizard School of Abjuration
  //   Arcane Ward (PHB p.115). Pool of HP that absorbs damage before regular
  //   HP. Max = 2 × wizard level + INT modifier. RAW does NOT recharge on
  //   rest — only refilled by casting an abjuration spell of L1+ (gain
  //   2 × spell level back). rechargeOn: 'never' tells RestPanel to skip it.
  //   Players manage via ClassResourcesPanel +/- buttons; auto-refill on
  //   abjuration cast is a future ticket if it proves valuable.
  'Wizard:School of Abjuration': [
    {
      id: 'arcane_ward',
      name: 'Arcane Ward',
      icon: '\u{1F6E1}️', // 🛡️
      rechargeOn: 'never',
      minLevel: 2,
      maxCalculation: 'wizardLevel*2+intMod',
      color: 'cyan',
      description: 'Damage absorber pool. Refilled by casting abjuration spells of L1+ (gain 2 × spell level back). Persists across rests.'
    }
  ],

  // [Phase 4b bucket 2 — APR 2026]: Wizard Arcane Recovery (PHB p.115).
  // Once per day after a short rest. Slot levels recovered up to ceil(level/2),
  // none higher than 5th. The pool size and slot-level cap is computed by
  // RestPanel at invoke-time; classResources tracks the 1/long-rest charge.
  Wizard: [
    {
      id: 'arcane_recovery',
      name: 'Arcane Recovery',
      icon: '✨', // ✨
      rechargeOn: 'long_rest',
      minLevel: 1,
      maxCalculation: 'fixed:1',
      color: 'purple',
      description: 'Recover spell slots after short rest (1/long rest)'
    }
  ],

  // Ranger, Rogue — no tracked numbered resources (Sneak Attack scales by formula, not a counter).
  // Druid Wild Shape is wired separately above.

  // [Q26 FIX 2026-05-16]: Artificer class resources (TCoE p.10-12).
  //   Pair-gap with Cleric (Q16) closed in this commit. Three trackable pools:
  //     1. Flash of Genius (L7): INT mod uses, min 1, recharge on long rest.
  //     2. Infuse Item active slots (L2): scales L2/L6/L10/L14/L18 → 2/3/4/5/6.
  //        rechargeOn 'long_rest' represents the "swap on long rest" semantic
  //        — RAW doesn't fully fit the rest-recharge model (infusions persist
  //        until replaced) but 'long_rest' is the closest available enum and
  //        matches the audit recommendation. A future refactor may introduce
  //        a 'replace_on_long_rest' semantic if needed.
  //     3. Spell-Storing Item charges (L11): 2 × INT mod, refresh when new
  //        spell stored (typically on a long rest per RAW).
  //   Requires two new maxCalculation formulas in `calculateResourceMax`:
  //   'intMod:1' (INT mod, min 1) and 'intMod2x' (2 × INT mod, min 1).
  Artificer: [
    {
      id: 'flash_of_genius',
      name: 'Flash of Genius',
      icon: '⚡',
      rechargeOn: 'long_rest',
      minLevel: 7,
      maxCalculation: 'intMod:1',
      color: 'blue',
      description: 'Reaction: add INT mod to any ability check or save within 30 ft (TCoE p.11)'
    },
    {
      id: 'infuse_item_active',
      name: 'Active Infusions',
      icon: '✨',
      rechargeOn: 'long_rest',
      minLevel: 2,
      maxCalculation: 'levelTable',
      maxTable: { 2: 2, 6: 3, 10: 4, 14: 5, 18: 6 },
      color: 'purple',
      description: 'Simultaneously active infusions imbued in items (TCoE p.10)'
    },
    {
      id: 'spell_storing_item',
      name: 'Spell-Storing Item',
      icon: '🔮',
      rechargeOn: 'long_rest',
      minLevel: 11,
      maxCalculation: 'intMod2x',
      color: 'purple',
      description: 'Charges in your stored-spell item (2 × INT mod, refreshes when new spell stored) — TCoE p.11'
    }
  ],
};

/**
 * [PURPOSE]: Get resource definitions for a class (including subclass resources)
 * [WHY]: Single lookup point used by RestPanel, ClassResourcesPanel, and custom content
 * @param {string} className - Class name (e.g., 'Fighter')
 * @param {string} subclass - Optional subclass name (e.g., 'Battle Master')
 * @returns {Array} Array of resource definitions
 */
export function getResourcesForClass(className, subclass) {
  const classResources = CLASS_RESOURCE_DEFINITIONS[className] || [];
  const subclassResources = subclass
    ? (CLASS_RESOURCE_DEFINITIONS[`${className}:${subclass}`] || [])
    : [];
  return [...classResources, ...subclassResources];
}

/**
 * [PURPOSE]: Calculate max value for a resource at a given class level
 * [WHY]: Encapsulates all max calculation logic in one place
 * @param {object} resourceDef - Resource definition from CLASS_RESOURCE_DEFINITIONS
 * @param {number} classLevel - Current class level
 * @param {object} character - Character data (for ability modifiers)
 * @returns {number} Max uses for this resource
 */
export function calculateResourceMax(resourceDef, classLevel, character = {}) {
  const calc = resourceDef.maxCalculation;

  if (calc === 'classLevel') return classLevel;
  if (calc === 'classLevel*5') return classLevel * 5;
  if (calc?.startsWith('fixed:')) return parseInt(calc.split(':')[1]) || 1;

  if (calc === 'chaMod:1') {
    const chaMod = Math.floor(((character.charisma || 10) - 10) / 2);
    return Math.max(1, chaMod);
  }

  if (calc === 'wisMod:1') {
    const wisMod = Math.floor(((character.wisdom || 10) - 10) / 2);
    return Math.max(1, wisMod);
  }

  if (calc === 'levelTable' && resourceDef.maxTable) {
    const thresholds = Object.keys(resourceDef.maxTable)
      .map(Number)
      .sort((a, b) => b - a);
    for (const threshold of thresholds) {
      if (classLevel >= threshold) return resourceDef.maxTable[threshold];
    }
    return 1;
  }

  // [Phase 4b BC ward — APR 2026]: Wizard Arcane Ward formula
  //   max = 2 × wizard level + INT modifier (PHB p.115).
  if (calc === 'wizardLevel*2+intMod') {
    const intMod = Math.floor(((character.intelligence || 10) - 10) / 2);
    return Math.max(1, classLevel * 2 + intMod);
  }

  // [Q26 FIX 2026-05-16]: Artificer Flash of Genius (TCoE p.11) — INT mod
  //   uses per long rest, minimum 1.
  if (calc === 'intMod:1') {
    const intMod = Math.floor(((character.intelligence || 10) - 10) / 2);
    return Math.max(1, intMod);
  }

  // [Q26 FIX 2026-05-16]: Artificer Spell-Storing Item (TCoE p.11) — 2 × INT
  //   mod charges. Min 1 because the feature is meaningless at 0 charges.
  if (calc === 'intMod2x') {
    const intMod = Math.floor(((character.intelligence || 10) - 10) / 2);
    return Math.max(1, intMod * 2);
  }

  return 1; // fallback
}

/**
 * [PURPOSE]: Resolve a level-keyed table value for a given class level
 * [WHY]: dieSize and slotLevelTable use { classLevel: value } format
 * @param {object} table - Level-keyed table (e.g., { 1: 'd6', 5: 'd8' })
 * @param {number} classLevel - Current class level
 * @returns {*} Value at the highest threshold <= classLevel, or null
 */
export function resolveLevelTable(table, classLevel) {
  if (!table) return null;
  const thresholds = Object.keys(table).map(Number).sort((a, b) => b - a);
  for (const threshold of thresholds) {
    if (classLevel >= threshold) return table[threshold];
  }
  return null;
}

/**
 * [PURPOSE]: Check if a resource recharges on a given rest type
 * [WHY]: Handles conditional recharge (e.g., Bard Font of Inspiration at level 5)
 * @param {object} resourceDef - Resource definition
 * @param {string} restType - 'short' or 'long'
 * @param {number} classLevel - Current class level
 * @returns {boolean}
 */
export function rechargesOnRest(resourceDef, restType, classLevel) {
  // [Phase 4b BC ward — APR 2026]: 'never' opts out of BOTH short and long
  //   rest recharge. Used for resources like Wizard School of Abjuration's
  //   Arcane Ward, which is refilled by casting abjuration spells (not by
  //   resting). Players manage these via the manual +/- buttons in
  //   ClassResourcesPanel.
  if (resourceDef.rechargeOn === 'never') return false;

  if (restType === 'long') return true; // Everything else recharges on long rest

  // Short rest
  if (resourceDef.rechargeOn === 'short_rest') return true;

  // Conditional: Bard Font of Inspiration at level 5
  if (resourceDef.conditionalRecharge === 'short_rest_at_5' && classLevel >= 5) return true;

  return false;
}

// ============================================================================
// [Fix 1.10 — APR 2026 release triage]: GLOBAL CUSTOM RESOURCE POOL REGISTRY
// ============================================================================
//
// [PURPOSE]: Custom feats can declare a Resource Pool (name + max + recharge).
//   Pre-fix the pool was captured INTO the feat object but never registered
//   anywhere players could reference it. User report:
//   "How is adding the name of the resource pool helpful — can I select it
//    anywhere else for my next custom feat? no — i cant — thats a gap!"
//
// [WHY HERE]: classResources.js is the canonical home for resource definitions
//   that drive RestPanel, ClassResourcesPanel, and the resource-aware UI.
//   Custom pools live in a separate module-level registry (NOT folded into
//   CLASS_RESOURCE_DEFINITIONS) because:
//     1. They aren't keyed by class — a player's custom Resource Pool isn't
//        attached to "Fighter" or "Wizard"; it stands on its own.
//     2. They're player-authored — they need a global add/remove API, not the
//        immutable static export shape used for SRD class resources.
//
// [PERSISTENCE]: This is an in-memory registry. Persistence is per-character —
//   on character load, CharacterContext walks playerStats.customFeats and
//   re-registers each feat's resourcePool by calling registerCustomResourcePool.
//   On feat save, FeatsPanel.saveCustomFeat calls registerCustomResourcePool.
//
// [STABLE IDs]: The pool id is minted once at first save (UUID-shape stamp,
//   not derived from the pool name) so renaming the pool doesn't fork it into
//   two registry entries.
//
// [IDEMPOTENCY]: Re-registering the same id overwrites — explicit guarantee
//   so character reload (which re-fires the register loop) doesn't dup pools.
const customResourcePools = {};

/**
 * Register (or overwrite) a custom resource pool in the global registry.
 *
 * @param {Object} pool
 * @param {string} pool.id        — Stable UUID-shape key (required)
 * @param {string} pool.name      — Display name, e.g., 'Sorcery Points' (required)
 * @param {number} pool.max       — Max charges (optional; some pools compute at use time)
 * @param {string} pool.recharge  — 'shortRest' | 'longRest' | 'dawn' | 'never'
 * @param {string} [pool.dieSize] — Optional die size, e.g., 'd6' / 'd8'
 * @param {number} [pool.quantity]— Optional quantity (alternative to max)
 * @param {string} [pool.color]   — Optional UI color theme
 * @param {string} [pool.description] — Optional tooltip text
 *
 * @returns {boolean} true if registered, false if rejected (missing id/name)
 */
export function registerCustomResourcePool(pool) {
  if (!pool || typeof pool !== 'object') return false;
  if (!pool.id || typeof pool.id !== 'string') return false;
  if (!pool.name || typeof pool.name !== 'string') return false;

  // [IDEMPOTENT]: Same id → overwrite. Reload loop calls register again
  //   for every saved pool; this guarantees the registry doesn't fork.
  customResourcePools[pool.id] = {
    id: pool.id,
    name: pool.name,
    max: pool.max,
    recharge: pool.recharge,
    dieSize: pool.dieSize,
    quantity: pool.quantity,
    color: pool.color,
    description: pool.description,
  };
  return true;
}

/**
 * Get all registered custom resource pools.
 * @returns {Array} Array of pool objects (may be empty)
 */
export function getCustomResourcePools() {
  return Object.values(customResourcePools);
}

/**
 * Get a single registered pool by id.
 * @param {string} id
 * @returns {Object|null}
 */
export function getCustomResourcePool(id) {
  if (!id || typeof id !== 'string') return null;
  return customResourcePools[id] || null;
}

/**
 * Remove a registered pool by id.
 * @param {string} id
 */
export function unregisterCustomResourcePool(id) {
  if (!id || typeof id !== 'string') return;
  delete customResourcePools[id];
}

/**
 * Clear the entire custom pool registry.
 * [WHY]: Used by tests and by full-character-replace flows to reset state.
 */
export function clearCustomResourcePools() {
  for (const key of Object.keys(customResourcePools)) {
    delete customResourcePools[key];
  }
}
